import React, { useEffect, useMemo, useState } from "react";
import DriverDeliveriesFilters from "../components/driver/DriverDeliveriesFilters";
import DriverDeliveriesSummary from "../components/driver/DriverDeliveriesSummary";
import DriverDeliveriesList from "../components/driver/DriverDeliveriesList";
import DriverDeliveriesAnalytics from "../components/driver/DriverDeliveriesAnalytics";
import DriverDeliveryDetailModal from "../components/driver/DriverDeliveryDetailModal";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";
import { useSearchParams } from "react-router-dom";
import type {
  DriverDeliveryCardItem,
  DriverDeliveryEnrichedRow,
  DriverDeliveryRow,
} from "../types/driverDeliveries";
import {
  buildDriverAnalyticsSummary,
  buildDriverDeliveryCards,
  buildDriverDeliveriesSummary,
  buildLegacyDriverName,
  enrichDriverDeliveries,
  formatMoney,
  getCurrentSettlementRange,
  getPreviousSettlementRange,
  nowISODate,
  resolveLegacyDriverName,
  withinRange,
} from "../utils/driverDeliveries";

type CurrentUserResponse = {
  ok?: boolean;
  user?: {
    id?: number | string;
    username?: string;
    name?: string;
    first_name?: string;
    last_name?: string;
    legacyDriverName?: string;
    rol?: string;
  };
  error?: string;
};

type UserRoleResponse = {
  ok?: boolean;
  rol?: string;
  data?: {
    id?: number | string;
    user_id?: number | string;
    vehicle?: string;
    vehicle_type?: string;
    tipo_vehiculo?: string;
  };
  error?: string;
};

type LegacyDriverResponse = {
  ok?: boolean;
  result?: DriverDeliveryRow[];
  error?: string;
};

const DRIVER_DATA_SOURCE: "legacy" | "current" = "legacy";
const DRIVER_COMMISSION_DISABLED = 0;

const pageStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
};

const contentGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) minmax(340px, 400px)",
  gap: "12px",
  alignItems: "start",
};

const stickyRightStyle: React.CSSProperties = {
  position: "sticky",
  top: "88px",
};

const loadingCardStyle: React.CSSProperties = {
  border: "1px solid var(--border)",
  borderRadius: "18px",
  background: "var(--surface)",
  boxShadow: "var(--shadow)",
  padding: "16px",
  color: "var(--text-secondary)",
  fontSize: "13px",
};

const emptyCardStyle: React.CSSProperties = {
  border: "1px solid var(--border)",
  borderRadius: "18px",
  background: "var(--surface)",
  boxShadow: "var(--shadow)",
  padding: "20px",
};

const emptyTitleStyle: React.CSSProperties = {
  fontSize: "16px",
  fontWeight: 800,
  color: "var(--text-primary)",
  marginBottom: "6px",
};

const emptyTextStyle: React.CSSProperties = {
  fontSize: "13px",
  color: "var(--text-secondary)",
  lineHeight: 1.55,
};

const warningStyle: React.CSSProperties = {
  border: "1px solid var(--border)",
  borderLeft: "4px solid var(--warning)",
  borderRadius: "14px",
  background: "var(--surface)",
  padding: "12px 14px",
  fontSize: "13px",
  color: "var(--text-secondary)",
};

const toTitleCase = (value: string): string => {
  return value
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

// LEGACY — heurístico por nombre, candidato a eliminación (ver misma nota
// en src/utils/driverDeliveries.ts / HomeDriver.tsx). Reemplazado por el
// vínculo determinístico driver_data.lightdata_username <->
// legacy.envios.cadete (server/legacy/driverLink/) vía
// GET /v2/drivers/me/legacy-envios. No se corta acá todavía porque
// requiere la misma migración de identidad corrida en el entorno real que
// sirve esta pantalla.
const buildLegacyDriverNameCandidates = (
  rawName: string,
  normalizedName?: string
): string[] => {
  const original = rawName.trim();
  const compact = rawName.replace(/\s+/g, " ").trim();
  const normalized = normalizedName?.replace(/\s+/g, " ").trim() || "";
  const doubleSpaced = compact.replace(/ /g, "  ");

  return Array.from(
    new Set(
      [
        original,
        compact,
        normalized,
        toTitleCase(compact),
        compact.toLowerCase(),
        compact.toUpperCase(),
        doubleSpaced,
        doubleSpaced.toLowerCase(),
        doubleSpaced.toUpperCase(),
      ].filter(Boolean)
    )
  );
};

const DriverDeliveries = () => {
  const { token, userId, username, firstName, lastName } = useAuth();
  const [searchParams] = useSearchParams();

  const requestedDate = searchParams.get("date");

  const initialDate =
    requestedDate && /^\d{4}-\d{2}-\d{2}$/.test(requestedDate)
      ? requestedDate
      : nowISODate();

  const [legacyDriverName, setLegacyDriverName] = useState<string>("");
  const [rawData, setRawData] = useState<DriverDeliveryRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [warning, setWarning] = useState<string>("");
  const [dateFrom, setDateFrom] = useState<string>(initialDate);
  const [dateTo, setDateTo] = useState<string>(initialDate);
  const [addressQuery, setAddressQuery] = useState<string>("");
  const [selectedItem, setSelectedItem] =
    useState<DriverDeliveryCardItem | null>(null);

  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number | "all">(10);

  const commission = DRIVER_COMMISSION_DISABLED;
  const commissionLabel = "0%";

  useEffect(() => {
    const fetchData = async () => {
      if (!token || !userId || !username) {
        setError("No se pudo identificar el driver autenticado.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");
        setWarning("");

        const userResponse = await authFetch(`/v2/users/${userId}`, { token });
        const userPayload = (await userResponse
          .json()
          .catch(() => ({}))) as CurrentUserResponse;

        const fallbackUser = {
          username,
          first_name: firstName,
          last_name: lastName,
        };

        const resolvedUser =
          userResponse.ok && userPayload?.user ? userPayload.user : fallbackUser;

        const resolvedFullName = buildLegacyDriverName(resolvedUser) || "Driver";
        const resolvedLegacyDriverName = resolveLegacyDriverName(resolvedUser);
        const candidates = buildLegacyDriverNameCandidates(
          resolvedFullName,
          resolvedLegacyDriverName
        );

        console.log("DriverDeliveries legacy lookup:", {
          authContext: {
            userId,
            username,
            firstName,
            lastName,
          },
          userEndpointOk: userResponse.ok,
          userEndpointUser: userPayload?.user,
          resolvedFullName,
          resolvedLegacyDriverName,
          candidates,
        });

        const roleResponse = await authFetch(`/v2/users/${userId}/role`, {
          token,
        });

        const rolePayload = (await roleResponse
          .json()
          .catch(() => ({}))) as UserRoleResponse;

        if (!roleResponse.ok || rolePayload?.ok === false) {
          throw new Error(
            rolePayload?.error ||
            "No se pudo resolver el driver asociado al usuario."
          );
        }

        if (DRIVER_DATA_SOURCE === "legacy") {
          let matchedRows: DriverDeliveryRow[] = [];
          let matchedName = candidates[0] || resolvedLegacyDriverName;

          for (const candidate of candidates) {
            const res = await authFetch("/v1/data/driver/me", {
              method: "POST",
              token,
              json: true,
              body: JSON.stringify({
                username: candidate,
              }),
            });

            const payload = (await res
              .json()
              .catch(() => ({}))) as LegacyDriverResponse;

            if (!res.ok || payload?.ok === false) {
              if (res.status === 401) {
                throw new Error(
                  "Sesión inválida o vencida. Volvé a iniciar sesión."
                );
              }

              if (res.status === 403) {
                throw new Error(
                  "Tu usuario no tiene permiso para ver estos envíos."
                );
              }

              throw new Error(payload?.error || "Error trayendo envíos");
            }

            const candidateRows = Array.isArray(payload.result)
              ? payload.result
              : [];

            if (candidateRows.length > 0) {
              matchedRows = candidateRows;
              matchedName = candidate;
              break;
            }
          }

          console.log("DriverDeliveries legacy matched:", {
            matchedName,
            totalRows: matchedRows.length,
          });

          setLegacyDriverName(matchedName);
          setRawData(matchedRows);
          return;
        }

        setLegacyDriverName(candidates[0] || resolvedLegacyDriverName);
        setRawData([]);
      } catch (err) {
        console.error(err);
        setError(
          err instanceof Error
            ? err.message
            : "No se pudieron cargar los envíos del driver."
        );
        setRawData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, userId, username, firstName, lastName]);

  const enrichedData = useMemo<DriverDeliveryEnrichedRow[]>(() => {
    return enrichDriverDeliveries(rawData, commission);
  }, [rawData, commission]);

  const filteredData = useMemo<DriverDeliveryEnrichedRow[]>(() => {
    const query = addressQuery.trim().toLowerCase();

    return enrichedData.filter((row) => {
      const inDateRange = withinRange(row._colecta_day, dateFrom, dateTo);
      if (!inDateRange) return false;

      if (!query) return true;

      const direccion = String(row.direccion || "").toLowerCase();
      return direccion.includes(query);
    });
  }, [enrichedData, dateFrom, dateTo, addressQuery]);

  const summary = useMemo(() => {
    return buildDriverDeliveriesSummary(filteredData);
  }, [filteredData]);

  const allCards = useMemo(() => {
    return buildDriverDeliveryCards(filteredData);
  }, [filteredData]);

  const analytics = useMemo(() => {
    return buildDriverAnalyticsSummary(filteredData);
  }, [filteredData]);

  useEffect(() => {
    setPage(1);
  }, [dateFrom, dateTo, addressQuery, pageSize]);

  const totalPages = useMemo(() => {
    if (pageSize === "all") return 1;
    return Math.max(1, Math.ceil(allCards.length / pageSize));
  }, [allCards.length, pageSize]);

  const paginatedCards = useMemo(() => {
    if (pageSize === "all") return allCards;
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return allCards.slice(start, end);
  }, [allCards, page, pageSize]);

  const handleDownloadXls = async () => {
    if (!token) return;

    if (!legacyDriverName) {
      alert("No se pudo resolver el nombre legacy del driver.");
      return;
    }

    try {
      const res = await authFetch("/v1/data/driver/print", {
        method: "POST",
        token,
        json: true,
        body: JSON.stringify({
          username: legacyDriverName,
        }),
      });

      if (!res.ok) {
        if (res.status === 401) {
          throw new Error("Sesión inválida o vencida. Volvé a iniciar sesión.");
        }

        if (res.status === 403) {
          throw new Error(
            "Tu usuario no tiene permiso para descargar el Excel. Backend debe permitir DRIVER en /v1/data/driver/print."
          );
        }

        const text = await res.text();
        throw new Error(text || "Error descargando Excel");
      }

      const blob = await res.blob();
      const link = document.createElement("a");
      const objectUrl = URL.createObjectURL(blob);
      const safeFileName = legacyDriverName.replace(/\s+/g, "_").toLowerCase();

      link.href = objectUrl;
      link.download = `envios_${safeFileName}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error(err);
      alert(
        err instanceof Error ? `❌ ${err.message}` : "❌ Error descargando Excel"
      );
    }
  };

  return (
    <div style={pageStyle}>
      <DriverDeliveriesFilters
        dateFrom={dateFrom}
        dateTo={dateTo}
        addressQuery={addressQuery}
        resultCount={filteredData.length}
        onChangeFrom={setDateFrom}
        onChangeTo={setDateTo}
        onChangeAddress={setAddressQuery}
        onToday={() => {
          const today = nowISODate();
          setDateFrom(today);
          setDateTo(today);
        }}
        onCurrentSettlement={() => {
          const range = getCurrentSettlementRange();
          setDateFrom(range.from);
          setDateTo(range.to);
        }}
        onPreviousSettlement={() => {
          const range = getPreviousSettlementRange();
          setDateFrom(range.from);
          setDateTo(range.to);
        }}
        onClear={() => {
          setDateFrom("");
          setDateTo("");
          setAddressQuery("");
        }}
        onDownloadXls={handleDownloadXls}
      />

      {warning ? <div style={warningStyle}>{warning}</div> : null}

      {loading ? (
        <div style={loadingCardStyle}>Cargando envíos...</div>
      ) : error ? (
        <div style={warningStyle}>{error}</div>
      ) : (
        <>
          <DriverDeliveriesSummary
            summary={summary}
            commissionLabel={commissionLabel}
            pickupTotal={0}
          />

          {allCards.length > 0 ? (
            <div style={contentGridStyle}>
              <DriverDeliveriesList
                items={paginatedCards}
                onSelect={(item) => setSelectedItem(item)}
                page={page}
                totalPages={totalPages}
                pageSize={pageSize}
                onChangePage={(nextPage) => {
                  if (nextPage < 1 || nextPage > totalPages) return;
                  setPage(nextPage);
                }}
                onChangePageSize={(value) => {
                  setPageSize(value);
                  setPage(1);
                }}
              />

              <div style={stickyRightStyle}>
                <DriverDeliveriesAnalytics analytics={analytics} />
              </div>
            </div>
          ) : (
            <div style={emptyCardStyle}>
              <div style={emptyTitleStyle}>No hay envíos en el rango</div>
              <div style={emptyTextStyle}>
                Probá cambiar las fechas o quitar los filtros. Chofer legacy:{" "}
                <strong>{legacyDriverName || "sin resolver"}</strong>. Total
                actual: {formatMoney(summary.totalACobrar)}.
              </div>
            </div>
          )}
        </>
      )}

      <DriverDeliveryDetailModal
        item={selectedItem}
        onClose={() => setSelectedItem(null)}
      />
    </div>
  );
};

export default DriverDeliveries;